import { Check } from 'lucide-react';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';
import { checkboxBoxClass } from './checkbox';

// Non modale: la pagina resta usabile col menu aperto, come i menu che sostituisce.
function DropdownMenu({
  modal = false,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root modal={modal} {...props} />;
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'surface-overlay z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-32 origin-(--radix-dropdown-menu-content-transform-origin) overflow-y-auto rounded-xl p-1.5 focus:outline-hidden',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuLabel({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      className={cn('px-2 py-1.5 text-caption font-medium text-subtle', className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn('-mx-1.5 my-1.5 border-t border-subtle', className)}
      {...props}
    />
  );
}

const itemClassName = cn(
  'relative flex w-full cursor-default items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-default select-none focus-visible:focus-ring-inset',
  'data-highlighted:bg-overlay-hover data-highlighted:text-strong',
  'data-disabled:pointer-events-none data-disabled:opacity-60'
);

function DropdownMenuItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return <DropdownMenuPrimitive.Item className={cn(itemClassName, className)} {...props} />;
}

/** Una voce con la sua casella in testa: resta nel menu quando si spunta. */
function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={cn(itemClassName, 'group/item data-[state=checked]:font-medium', className)}
      onSelect={(event) => event.preventDefault()}
      {...props}
    >
      <span
        className={cn(
          checkboxBoxClass,
          'group-data-[state=checked]/item:border-transparent group-data-[state=checked]/item:bg-primary'
        )}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <Check aria-hidden className="h-3 w-3" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

/** Una scelta singola: la spunta a destra sulla voce scelta. */
type DropdownMenuRadioItemProps = ComponentProps<typeof DropdownMenuPrimitive.RadioItem> & {
  indicatorClassName?: string;
};

function DropdownMenuRadioItem({
  className,
  children,
  indicatorClassName,
  ...props
}: DropdownMenuRadioItemProps) {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(itemClassName, 'data-[state=checked]:font-medium', className)}
      {...props}
    >
      {children}
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className={cn('h-4 w-4 shrink-0', indicatorClassName)} aria-hidden />
      </DropdownMenuPrimitive.ItemIndicator>
    </DropdownMenuPrimitive.RadioItem>
  );
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
